import { BaseService } from './BaseService'
import {
  BrandAppearance, RefZone, ExclusionReason, ReviewBrand
} from './broadcastAdminService'

/**
 * The CUSTOMER view of broadcast analysis. Read-only by construction: there is
 * no create, update or delete here because uploads are support-driven and
 * results are published by a reviewer, not produced on demand.
 *
 * Separate from broadcastAdminService on purpose — that one can see
 * unpublished broadcasts, costs and reviewer exclusions.
 */

export interface PublishedBroadcast {
  id: string
  title: string
  season: string | null
  event_date: string | null
  duration_ms: number | null
  width: number | null
  height: number | null
  credits_charged: number | null
  reviewed_at: string | null
  sport_name: string | null
  league_short_name: string | null
  venue_name: string | null
  home_team_name: string | null
  away_team_name: string | null
  competition_stage_name: string | null
  child_count: number
  /**
   * Valuation inputs. NULL is the ordinary state, not a failure: nothing
   * captures these on upload yet, so every broadcast analysed so far has none
   * and the report renders its exposure without money. See backlog #39.
   */
  viewers: number | null
  cpm: number | null
  currency: string | null
  /** audience / 1000 x CPM, derived server-side so there is one definition. */
  rate_per_30s: number | null
  /** The CPM actually priced at: this broadcast's own, else its rate card's. */
  effective_cpm?: number | null
  /** The override, else the rate card's, else USD. What money is shown in. */
  effective_currency?: string | null
  /** 'negotiated_cpm' (set on this broadcast) or 'rate_card'; null if neither. */
  valuation_basis?: 'negotiated_cpm' | 'rate_card' | null
  rate_card_name?: string | null

  /** Who carried the feed. Null when not recorded. */
  channel_name?: string | null
  channel_type?: 'linear' | 'svod' | 'fast' | 'free_stream' | 'social' | 'other' | null
  feed_role?: string | null
  /** Where the viewers figure came from, and whether it is an estimate. */
  viewership_source_name?: string | null
  viewers_is_estimate?: boolean | null

  /**
   * Exposure rollups, present on the LIBRARY list only, so the page can show
   * aggregates without fetching every broadcast in full. A rain-split game's
   * children are folded into its parent's figures, because the library lists
   * only parents.
   *
   * Zero means analysed with nothing found. The frame counts are nullable
   * instead, because a missing frames row means never measured — which is a
   * different thing and must not divide into a percentage.
   */
  counted_area_seconds?: number
  sponsored_area_seconds?: number
  sponsored_brands?: number
  frames_analysed?: number | null
  frames_with_sponsor?: number | null
  /**
   * Sponsored area-seconds split by sponsor (key = partner id) and by
   * position (key = zone code). Area, not money: the client prices each at
   * this broadcast's own rate. LIBRARY list only; slices sum to
   * sponsored_area_seconds.
   */
  sponsor_area?: AreaSlice[]
  zone_area?: AreaSlice[]
  /** Each sponsor's area by position (key = zone code). LIBRARY list only;
   *  a sponsor's slices sum to its sponsor_area entry. */
  sponsor_zone_area?: SponsorZoneSlice[]
}

export interface SponsorZoneSlice extends AreaSlice {
  partner_id: string
}

export interface AreaSlice {
  key: string
  label: string | null
  area_seconds: number
}

/**
 * Identical to the Support ReviewBrand: customers now review the same rows
 * with the same controls, so a second shape would only drift.
 */
export type ExposureBrand = ReviewBrand

export interface ExposureZone {
  zone_code: string
  zone_label: string
  brands: number
  total_area_seconds: number
}

export interface ExposureCoverage {
  zone_code: string
  zone_label: string
  named: number
  unreadable: number
  pct_unreadable: number
}

/** NULL for a broadcast analysed before per-frame records existed. Absent
 *  means NOT MEASURED — never render it as zero. */
export interface ExposureFrames {
  analysed: number
  withDetections: number
  detections: number
  /**
   * Frames in which at least one SPONSORED brand was on screen.
   *
   * Zero is a real answer here — no sponsor appeared — unlike the whole
   * object being null, which means nobody measured. Do not collapse them.
   */
  withSponsored: number
}

export interface ExposureScene {
  scene: string
  frames: number
}

/** One bar of the exposure timeline. */
export interface ExposureBucket {
  /** Milliseconds from the top of the broadcast. */
  start_ms: number
  /** Frames sampled in this bucket — the denominator, which can be short at
   *  the end of a file. Dividing by the nominal width would draw a dip where
   *  there is only less footage. */
  frames: number
  /** Of those, how many had at least one sponsored brand on screen. */
  sponsor_frames: number
  /** What the camera was mostly doing: the modal scene. */
  scene: string | null
}

export interface ExposureTimeline {
  bucketMs: number
  buckets: ExposureBucket[]
}

export interface BroadcastExposure {
  broadcast: PublishedBroadcast
  brands: ExposureBrand[]
  zones: ExposureZone[]
  coverage: ExposureCoverage[]
  frames: ExposureFrames | null
  scenes: ExposureScene[]
  timeline: ExposureTimeline
}

/** What the allowance banner needs. Deliberately less than Support sees. */
export interface LibraryEntitlement {
  hasAllowance: boolean
  level: 'ok' | 'warning' | 'exhausted'
  purchased: number
  used: number
  remaining: number
  periodLabel: string | null
  periodEnd: string | null
}

interface Wrapped<T> { success: boolean; data: T; message?: string }

export interface PlaybackTarget {
  url: string
  expiresAt: string
  durationMs: number | null
  bytes: number | null
}

class BroadcastService extends BaseService {
  constructor() {
    super('cro')
  }

  async listBroadcasts(customerId: string): Promise<Wrapped<PublishedBroadcast[]>> {
    return this.get<Wrapped<PublishedBroadcast[]>>('/broadcast/library', { customerId })
  }

  async getEntitlement(customerId: string): Promise<Wrapped<LibraryEntitlement>> {
    return this.get<Wrapped<LibraryEntitlement>>('/broadcast/library/entitlement', { customerId })
  }

  /**
   * A short-lived signed URL for the seekable copy. Published broadcasts only
   * — the server enforces that, this just asks.
   *
   * Throws with status 409 while the copy is still being remuxed, which the
   * player renders as "preparing" rather than as a failure.
   */
  async getPlaybackUrl(customerId: string, id: string): Promise<Wrapped<PlaybackTarget>> {
    return this.get<Wrapped<PlaybackTarget>>(`/broadcast/library/${id}/playback-url`, { customerId })
  }

  async getBroadcast(customerId: string, id: string): Promise<Wrapped<BroadcastExposure>> {
    return this.get<Wrapped<BroadcastExposure>>(`/broadcast/library/${id}`, { customerId })
  }

  async getBrandAppearances(
    customerId: string, id: string, brandName: string
  ): Promise<Wrapped<BrandAppearance[]>> {
    return this.get<Wrapped<BrandAppearance[]>>(
      `/broadcast/library/${id}/brands/${encodeURIComponent(brandName)}/appearances`,
      { customerId }
    )
  }

  async setBrandExclusion(
    customerId: string, id: string, brandName: string, excludedReason: ExclusionReason | null
  ): Promise<Wrapped<unknown>> {
    return this.put(
      `/broadcast/library/${id}/brands/${encodeURIComponent(brandName)}/exclusion`,
      { excludedReason }, { customerId }
    )
  }

  async setAppearanceZone(
    customerId: string, id: string, appearanceId: string, zoneCode: string
  ): Promise<Wrapped<unknown>> {
    return this.put(
      `/broadcast/library/${id}/appearances/${appearanceId}/zone`,
      { zoneCode }, { customerId }
    )
  }

  async mergeBrand(
    customerId: string, aliasKey: string, intoKey: string, canonicalName?: string
  ): Promise<Wrapped<unknown>> {
    return this.post('/broadcast/library/merges',
      { aliasKey, intoKey, canonicalName: canonicalName ?? null }, { customerId })
  }

  async unmergeBrand(customerId: string, aliasKey: string): Promise<Wrapped<unknown>> {
    return this.delete(`/broadcast/library/merges/${encodeURIComponent(aliasKey)}`, { customerId })
  }

  async listZones(broadcastId?: string): Promise<Wrapped<RefZone[]>> {
    // Scoped to the broadcast's sport: a reviewer correcting a
    // basketball zone must not be offered `dugout`, and the model was
    // never shown it either.
    return this.get<Wrapped<RefZone[]>>('/broadcast/reference/zones',
      broadcastId ? { broadcastId } : undefined)
  }

  getFrameUrl(customerId: string, id: string, appearanceId: string): string {
    return `${this.baseURL}/broadcast/library/${id}/frame`
      + `?customerId=${customerId}&appearanceId=${appearanceId}`
  }
}

export const broadcastService = new BroadcastService()

/** The customer's view: their own published broadcasts only. */
export const customerReviewSource = {
  getFrameUrl: (c: string, b: string, a: string) => broadcastService.getFrameUrl(c, b, a),
  getPlaybackUrl: (c: string, b: string) => broadcastService.getPlaybackUrl(c, b),
  getAppearances: (c: string, b: string, n: string) => broadcastService.getBrandAppearances(c, b, n),
  setZone: (c: string, b: string, a: string, z: string) =>
    broadcastService.setAppearanceZone(c, b, a, z),
  listZones: (broadcastId?: string) => broadcastService.listZones(broadcastId),
  mergeBrand: (c: string, alias: string, into: string, name?: string) =>
    broadcastService.mergeBrand(c, alias, into, name),
  unmergeBrand: (c: string, alias: string) => broadcastService.unmergeBrand(c, alias)
}
export default broadcastService
